import {
  compose,
  split,
  partition,
  project,
  replace,
  map,
  join,
} from 'ramda';

import {
  trimTrailingDash,
  trimTrailingSlash,
  recordsWithValue,
  removeNilProps,
  isBodyStyle,
  isColor,
  isYear,
  canBeIdentifiedAsTaxonomy,
  complexFeatures,
  complexMakes,
  hasLocationInUrl,
  skipValue,
  fastNormalizeUrl,
  removeNonRelevantUrlPortions,
  urlEndsWithState,
  isResultLocationOnly,
  capitalizeWords,
  splitDash
} from './functions';

import {
  regexMatchUrlWithLocation,
  regexMatchUrlWithLocationNoState,
  regexUrl,
  defaultCarsRegex,
  locationRegex,
  defaultSearchResult,
  startValueRank,
  projectedResultProps,
  locationRank,
  rankDecrementStep,
  taxonomyRank
} from './constants';

const toTitleCase = compose(
  join(' '),
  capitalizeWords,
  split(' ')
);
const formatTaxonomy = compose(
  toTitleCase,
  replace(/-/g, ' ')
);

const toComplex = x => replace(x.simplified, x.complex);

const complexifyPipe = compose(map(toComplex));

const denormalizeTaxonomy = compose(...complexifyPipe(complexMakes));
const denormalizeFeatures = compose(...complexifyPipe(complexFeatures));

const fastNormalizeField = compose(
  trimTrailingDash,
  trimTrailingSlash
);

const mapTermsWeights = minLength => initialValue => terms => {
  let currentValue = initialValue;

  const results = terms
    .map(x => {
      const doSkip = (minLength && x.length < minLength) || skipValue(x);

      const result = {
        value: x,
        rank: currentValue,
        doSkip,
        isRequired: false
      };

      if (!doSkip) {
        currentValue -= rankDecrementStep;
      }

      return result;
    })
    .filter(x => !x.doSkip);

  return {
    results,
    currentValue
  };
};

const splitMapArray = minLength => initialValue => terms => {
  const { results, currentValue } = mapTermsWeights(minLength)(initialValue)(
    terms
  );

  return {
    results,
    lastWeight: currentValue
  };
};

const splitMap = minLength => initialValue => text =>
  splitMapArray(minLength)(initialValue)(splitDash(text));
const splitMapLocation = splitMap(2);
const splitMapFeatures = splitMap();

const partitionRemainder = text => {
  const splitTerms = splitDash(text).map(x => ({
    value: x,
    isColor: isColor(x),
    isYear: isYear(x),
    isBodyStyle: isBodyStyle(x)
  }));

  const p = partition(
    x => !x.isColor && !x.isYear && !x.isBodyStyle,
    splitTerms
  );

  return {
    colors: splitTerms.filter(x => x.isColor),
    years: splitTerms.filter(x => x.isYear),
    bodyStyles: splitTerms.filter(x => x.isBodyStyle),
    otherTerms: p[0]
  };
};

const parseRemainder = remainder => {
  const {
    otherTerms = [],
    bodyStyles = [],
    colors = [],
    years = []
  } = remainder ? partitionRemainder(remainder) : [];
  const reducedOtherTerms =
    otherTerms.length &&
    otherTerms.reduce((p, c) => `${p}${p ? '-' : ''}${c.value}`, '');

  return {
    otherTerms,
    bodyStyles,
    colors,
    years,
    reducedOtherTerms
  };
};

export const splitUrlFromLocation = url => {
  const relevantUrl = removeNonRelevantUrlPortions(url);

  const [, urlRest, location, city, state] = urlEndsWithState(relevantUrl)
    ? regexMatchUrlWithLocation.exec(relevantUrl) || []
    : regexMatchUrlWithLocationNoState.exec(relevantUrl) || [];

  return {
    urlRest,
    location,
    city,
    state
  };
};

const swapRemainderWithTaxonomy = result => {
  // eslint-disable-next-line prefer-destructuring
  result[1] = result[2];
  result[2] = undefined;

  return result;
};

export const dissectUrl = url => {
  const relevantUrl = removeNonRelevantUrlPortions(url);

  let result;

  if (hasLocationInUrl(url)) {
    const { urlRest, location } = splitUrlFromLocation(relevantUrl);

    result = regexUrl.exec(urlRest);

    result.push(location);
  } else {
    result = regexUrl.exec(relevantUrl);

    result.push(undefined);
  }

  const r = parseRemainder(result[2]);

  if (canBeIdentifiedAsTaxonomy(r.otherTerms)) {
    swapRemainderWithTaxonomy(result);
  }

  result.forEach((x, i) => {
    if (result[i]) {
      result[i] = trimTrailingSlash(x);
    }
  });

  return result;
};

const parseUrl = urlParam => {
  const url = fastNormalizeUrl(urlParam);

  let splitLocation;

  if (hasLocationInUrl(url)) {
    splitLocation = splitUrlFromLocation(url);
  } else {
    splitLocation = {
      urlRest: url,
      location: undefined,
      city: undefined,
      state: undefined
    };
  }

  const { urlRest, location } = splitLocation;

  const [, taxonomy, other, withFeatures] = regexUrl.exec(urlRest);

  const withFeaturesNormalized =
    withFeatures && fastNormalizeField(withFeatures);
  const locationNormalized = location && fastNormalizeField(location);
  const remainderNormalized = other && fastNormalizeField(other);

  const {
    otherTerms,
    bodyStyles,
    colors,
    years,
    reducedOtherTerms
  } = parseRemainder(remainderNormalized);

  return {
    originalUrl: urlParam,
    taxonomy,
    other,
    remainderNormalized,
    withFeatures,
    withFeaturesNormalized,
    locationNormalized,

    otherTerms,
    bodyStyles,
    colors,
    years,
    reducedOtherTerms
  };
};

const getLocation = locationNormalized => {
  if (!locationNormalized) {
    return {
      records: []
    };
  }

  if (locationRegex.test(locationNormalized)) {
    const [, city, state] = locationRegex.exec(locationNormalized);

    return {
      records: [{ rank: locationRank, value: city, isRequired: false, isLocation: true }],
      city,
      state
    };
  }

  const { results } = splitMapLocation(locationRank)(locationNormalized);
  const locationMap = results.filter(x => x.value.length && x.value.length > 2);

  return {
    records: locationMap || []
  };
};

const getProjection = (
  taxonomyValue,
  trimValue,
  otherMap,
  featuresMap,
  records
) => {
  const mapResult = recordsWithValue([
    taxonomyValue,
    trimValue,
    ...(otherMap || []),
    ...(featuresMap || []),
    ...(records || [])
  ]);

  return project(projectedResultProps, mapResult);
};

const getTaxonomyResult = taxonomyValue => {
  const taxonomyText = taxonomyValue && taxonomyValue.value;
  let formattedTaxonomyText;

  if (taxonomyText) {
    formattedTaxonomyText = formatTaxonomy(taxonomyText);
  }

  return {
    raw: taxonomyText,
    formatted: formattedTaxonomyText
  };
};

export const dissectUrlWithWeightsDefault = url => {
  return {
    _calc: {
      originalUrl: url,
      withFeatures: undefined,
      taxonomy: undefined,
      location: undefined,
      colors: [],
      years: [],
      other: undefined
    },
    result: defaultSearchResult,
    taxonomy: { raw: undefined, formatted: undefined },
    location: { city: undefined, state: undefined }
  };
};


// if the final result is not enough search terms then we need to
// supplement some additional basic search terms
const verifySufficientTerms = projectedResult => {
  const cleanProjection = removeNilProps(projectedResult);
  let result;

  if (isResultLocationOnly(cleanProjection)) {
    result = [
      ...defaultSearchResult,
      ...cleanProjection,
    ]
  } else {
    result = cleanProjection;
  }

  return result;
}

const weightedValue = (weight, isRequired = false) => value => {
  return (
    value && {
      value: fastNormalizeField(value),
      rank: weight,
      isRequired
    }
  );
};

const getTaxonomy = weightedValue(taxonomyRank, true);

export const dissectUrlWithWeights = url => {
  const {
    originalUrl,
    taxonomy,
    remainderNormalized,
    withFeaturesNormalized,
    locationNormalized,

    otherTerms,
    bodyStyles,
    colors,
    years,
    reducedOtherTerms
  } = parseUrl(url);

  let otherMap;
  let featuresMap;
  let currentWeight = startValueRank;
  let remainder = remainderNormalized;

  let taxonomyValue = getTaxonomy(taxonomy);

  const isTaxonomy = canBeIdentifiedAsTaxonomy(otherTerms);
  const trim = !isTaxonomy && reducedOtherTerms;

  if (defaultCarsRegex.exec(url)) {
    return dissectUrlWithWeightsDefault(originalUrl);
  }

  if (!taxonomyValue && isTaxonomy) {
    if (bodyStyles.length) {
      taxonomyValue = getTaxonomy(
        `${reducedOtherTerms}-${bodyStyles[0].value}`
      );
    } else {
      taxonomyValue = getTaxonomy(reducedOtherTerms);
    }
  }

  const trimValue = {
    value: trim,
    rank: currentWeight,
    isRequired: false
  };

  if (trim) {
    currentWeight -= rankDecrementStep;
  }

  // bodystyle is actually taxonomy but may have appeared as a remainder
  if (!taxonomyValue && bodyStyles.length) {
    taxonomyValue = getTaxonomy(bodyStyles[0].value);
  }

  if (taxonomyValue) {
    taxonomyValue.value = denormalizeTaxonomy(taxonomyValue.value);
  }
  if (remainder) {
    remainder = denormalizeTaxonomy(remainder);
  }

  const colorsYears = [...years, ...colors];

  if (colorsYears.length) {
    const { results, lastWeight } = splitMapArray()(currentWeight)(
      colorsYears.map(x => x.value)
    );

    currentWeight = lastWeight;
    otherMap = results;
  }

  if (withFeaturesNormalized) {
    const { results, lastWeight } = splitMapFeatures(currentWeight)(
      withFeaturesNormalized
    );

    currentWeight = lastWeight;

    featuresMap = results.map(x => ({
      ...x,
      value: denormalizeFeatures(x.value)
    }));
  }

  const { records, city, state } = getLocation(locationNormalized);

  const _calc = {
    originalUrl,
    withFeatures:
      withFeaturesNormalized && denormalizeFeatures(withFeaturesNormalized),
    taxonomy: taxonomyValue,
    location: locationNormalized,
    colors,
    years,
    other: remainder
  };

  const projectedResult = getProjection(
    taxonomyValue,
    trimValue,
    otherMap,
    featuresMap,
    records
  );

  const verifiedResult = verifySufficientTerms(projectedResult);

  const taxonomyObject = getTaxonomyResult(taxonomyValue);

  return {
    _calc,
    result: verifiedResult,
    taxonomy: taxonomyObject,
    location: {
      city,
      state
    }
  };
};

